import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import api from "../../api";
import ChartInfoPopup from "./ChartInfoPopup";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const HourlyActiveUsersChart = () => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });

  const [last1HrUsers, setLast1HrUsers] = useState(0);
  const [last12HrUsers, setLast12HrUsers] = useState(0);

  const [themeColors, setThemeColors] = useState({});

  // VKD47, VKD43 Started

  const formatCountLabel = (count, singular, plural) => {
    if (count === 0) return `No ${plural}`;
    if (count === 1) return `1 ${singular}`;
    return `${count} ${plural}`;
  };

  // VKD47, VKD43 End

  // read css variables
  const loadThemeColors = () => {
    const styles = getComputedStyle(document.documentElement);
    return {
      text: styles.getPropertyValue("--dark-color").trim(),
      grid: styles.getPropertyValue("--border-color").trim(),
      bg: styles.getPropertyValue("--background-color").trim(),
    };
  };

  useEffect(() => {
    setThemeColors(loadThemeColors());

    const observer = new MutationObserver(() => {
      setThemeColors(loadThemeColors());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token =
          localStorage.getItem("access_token") ||
          sessionStorage.getItem("access_token");

        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        const baseUrl = process.env.REACT_APP_API_URL;
        const res = await api.get(
          `${baseUrl}/chat/hourly-active-users`,
          config,
        );

        const { hourlyBreakdown, userInLast1Hour, userInLast12Hours } =
          res.data;

        const labels = hourlyBreakdown.map((item) => item.hour); // ["1Hr", "2Hr", ..., "12Hr"]
        const data = hourlyBreakdown.map((item) => item.count); // [0, 0, ..., 1]

        setLast1HrUsers(userInLast1Hour);
        setLast12HrUsers(userInLast12Hours);

        setChartData({
          labels,
          datasets: [
            {
              label: "Users",
              data,
              backgroundColor: "#6B6B6B",
              barThickness: 14,
              borderRadius: 2,
            },
          ],
        });
      } catch (err) {
        console.error("❌ Failed to fetch hourly active users:", err);
      }
    };

    fetchData();
  }, []);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index",
        intersect: false,
        // VKD47, VKD43 Started
        callbacks: {
          label: (context) => formatCountLabel(context.raw, "User", "Users"),
        },
        // VKD47, VKD43 End
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 12 } },
        title: {
          display: true,
          text: "Time Period",
          color: themeColors.text,
          font: { size: 13, weight: "bold" },
          padding: { top: 10 },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          drawTicks: false,
          color: "#e0e0e0",
        },
        ticks: {
          display: true,
          font: { size: 12 },
          stepSize: 10, // 🟢 Y-axis value jumps 10 → 20 → 30 → 40 ...
        },
        title: {
          display: true,
          text: "Number of Users",
          color: themeColors.text,
          font: { size: 13, weight: "bold" },
          padding: { bottom: 10 },
        },
      },
    },
  };

  return (
    <div className="chart-card p-3 shadow-sm rounded mb-4 profile-i d-flex flex-column">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div className="d-flex align-items-center gap-2 mb-3">
          <h5 className="fw-semibold m-0">
            <span className="border-start border-4 ps-2 ak-text-lg d-flex align-content-center">
              User Traffic
            </span>
          </h5>

          <div className="chart-info-anchor">
            <ChartInfoPopup text="This chart displays how many unique users interacted with the chatbot in each of the last 12 hours. The Y-axis represents number of users, and the X-axis represents hourly intervals. The summary highlights total users in the last hour and last 12 hours. It helps you understand short-term user activity and identify hourly engagement patterns." />
          </div>
        </div>

        <div className="text-end ak-text-lg">
          <div className="d-flex gap-3">
            <div className="text-center">
              <div>In last 1 hour</div>
              <div className="fw-bold">{last1HrUsers}</div>
            </div>
            <div className="vr" />
            <div className="text-center">
              <div>In last 12 hours</div>
              <div className="fw-bold">{last12HrUsers}</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: "300px", width: "100%", marginTop: "auto" }}>
        {/* <Bar data={data} options={options} /> */}
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export default HourlyActiveUsersChart;
